import React, { PureComponent } from 'react';
import { TimeToPrice } from '~/Api/Analytics/VirtualBrand/TimeToPrice';
import { formatHumanDate } from '~/DateTime';
import FormattedPrice from '~/FormattedProductPrice';
import Tooltip from '~/UI/Tooltip/Tooltip';

import styles from './styles.less';

interface Props {
	prices: TimeToPrice[];
	maxStepsCount: number;
	barHeight: number;
}

export default class Chart extends PureComponent<Props> {
	static defaultProps = {
		maxStepsCount: 8,
		barHeight: 370,
	};

	renderUnavailableText(price: TimeToPrice) {
		if (price.hasNoPriceSet) {
			return 'N/A - Price not available';
		}
		if (price.hasUnavailableData) {
			return 'N/A - Historic datasets unavailable';
		}
		return 'N/A';
	}

	render() {
		const { prices, maxStepsCount, barHeight } = this.props;
		const daysCount = prices.reduce((maxDays, price) => Math.max(maxDays, price.minDays ?? 0), 0) || 80;
		const base = 10 ** ((Math.log(daysCount - 1) * Math.LOG10E) | 0);
		const lastStep = Math.ceil(daysCount / base) * base;
		const step = Math.ceil(lastStep / maxStepsCount / base) * base;
		const stepsCount = Math.round(lastStep / step);
		const steps = Array.from(Array(stepsCount + 1).keys()).reverse();
		const legendHeight = 100 / stepsCount;

		return (
			<div className={styles.chart}>
				<div className={styles.axisLeft} style={{ height: barHeight - 10 }}>
					{steps.map((i) => (
						<div className={styles.axisLeftItem} key={i} style={{ height: `${legendHeight}%` }}>
							<span>{step * i} days</span>
						</div>
					))}
				</div>
				<div className={styles.content}>
					<div className={styles.contentScroll} style={{ height: barHeight + 30, minWidth: prices.length * 30 + 4 }}>
						{prices.map((price) => {
							const percentage = (100 / lastStep) * (price.minDays ?? 0);
							const textHeight = barHeight * (1 - percentage / 100);

							const column = (
								<div className={styles.bar} key={price.country.alpha2}>
									<div className={styles.barValue} style={{ height: `${percentage}%` }}>
										{price.minDays ? (
											<div className={textHeight < 27 ? styles.barValueLegendNegative : styles.barValueLegend}>
												{price.minDays}
											</div>
										) : (
											<div className={styles.barUnvailableLegend}>{this.renderUnavailableText(price)}</div>
										)}
									</div>
									<div className={styles.barLegend}>{price.country.alpha2}</div>
								</div>
							);

							return price.times?.length > 0 ? (
								<Tooltip key={`tooltip-${price.country.alpha2}`} reference={column}>
									{price.times?.map((time) => (
										<div
											className={styles.popoverItem}
											key={`tooltip-div-${price.country.alpha2}-${time.dataSource?.type || time.since.toISOString()}-${
												time.days
											}`}
										>
											<strong>{time.dataSource ? time.dataSource.shortName : 'Other source'}</strong>
											<br />
											{formatHumanDate(time.since)}
											<br />
											<FormattedPrice
												rounded
												dataSource={time.dataSource}
												isFetching={false}
												productPrice={time.price}
											/>{' '}
											{time.price?.humanCondensedFormatType()}
											<br />
											{time.status.isRemoved ? (
												<p className={styles.warningText}>Product is no longer present in current dataset.</p>
											) : null}
										</div>
									))}
								</Tooltip>
							) : (
								column
							);
						})}
					</div>
				</div>
			</div>
		);
	}
}
